export class FileApiService {
  resource = '/api/file'
  baseUrl!: string
  token!: string

  constructor(baseUrl: string, token: string) {
    this.baseUrl = baseUrl + this.resource
    this.token = token
  }

  async uploadFile(path: string, file: File) {
    const headers = new Headers()
    headers.append('Authorization', this.token ? `Bearer ${this.token}` : '')

    const formData = new FormData()
    formData.append('file', file, file.name)

    const url = `${this.baseUrl}${path}`

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: formData
    })

    if (response.ok) {
      return true
    }

    const text = await response.text()
    throw text
  }

  async downloadFile(path: string, filename: string) {
    const uri = `${this.baseUrl}${path}`
    const headers = new Headers()
    headers.append('Authorization', this.token ? `Bearer ${this.token}` : '')
    const response = await fetch(uri, { headers: headers })

    if (!response.ok) {
      const error = await response.text()
      throw error
    }

    const contentDisposition = response.headers.get('Content-Disposition')
    if (contentDisposition) {
      // this regex is to make sure it captures the filename correctly in between the quotes
      const filenameMatch = contentDisposition.match(/filename\*?="?([^";]+)"?/)

      if (filenameMatch && filenameMatch[1]) {
        const extractedFilename = filenameMatch[1]

        // In the content-disposition header, the values contain "filename=filename1234.txt" and sometimes "filename*=UTF8_filename1234.txt" or "filename_=filename1234"
        // We want only want to extract the 'filename=' value and exclude "filename_=" or "filename*="
        // 'filename=' has the correct filename
        if (!extractedFilename.includes('filename_') && !extractedFilename.includes('filename*')) {
          filename = extractedFilename
        }
      }
    }

    this.downloadBlob(await response.blob(), filename)
  }

  /**
   * Generates a PDF file from the provided data and triggers a download with the specified filename.
   * @param data The data to be used for generating the PDF.
   * @param filename The desired filename for the downloaded PDF, excluding the .pdf extension.
   */
  async generatePdf(data: PdfRequest, filename: string) {
    const uri = `${this.baseUrl}/generatepdf`
    const headers = new Headers()
    headers.append('Authorization', this.token ? `Bearer ${this.token}` : '')
    headers.append('Content-Type', 'application/json')

    const response = await fetch(uri, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.text()
      throw error
    }

    // Typically, the browser can automatically infer and add the .pdf file extension based on e.g. response headers. However, if the filename contains a period (e.g. when the build number contains one),
    // the browser will treat the dot as the extension separator without adding .pdf. To ensure the file is downloaded with the correct .pdf extension, we explicitly append it to the filename.
    this.downloadBlob(await response.blob(), `${filename}.pdf`)
  }

  private downloadBlob(data: Blob, filename: string) {
    const a = document.createElement('a')
    a.href = URL.createObjectURL(data)
    a.download = data.type === 'application/vnd.ms-outlook' ? filename + '.msg' : filename
    a.click()
    a.remove()
  }
}

type PdfRequest = {
  templateModelType: string
  [key: string]: unknown
}
